import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { requireStore } from '../shared/require-store';

/**
 * #118 — pre-send hydration guard for media→group sends.
 *
 * WhatsApp Web keeps dormant groups as hollow chat models (no name, no
 * groupMetadata.participants). Text sends tolerate that; the media
 * compose path (`sendMediaMsgToChat`) fails with the opaque
 * `messageSendResult: ERROR_UNKNOWN`. Probing affected engines showed two
 * distinct populations behind that error:
 *
 *  1. hydratable-dormant — the account IS still a member; one server-side
 *     `GroupQueryAndUpdate` populates the model and the send can proceed;
 *  2. dead-membership — the server returns no metadata (WhatsApp only
 *     reveals group metadata to members), so no send can ever succeed.
 *
 * This guard turns (1) into a successful first-attempt send and (2) into
 * an immediate, customer-readable failure instead of an ERROR_UNKNOWN
 * retry loop.
 *
 * Failure containment: the guard must never break a send that would have
 * worked without it — a throwing or hanging metadata query falls through
 * to the pre-guard behavior (attempt the send as-is).
 *
 * #94 amendment (2026-08-04) — that fall-through is now CONDITIONAL. Once the
 * send-trace stopped being truncated at 300 chars we could finally see what
 * happens after a hydration timeout, and the answer was: nothing good. The
 * trace reads `media:groupGuard=8004` (this timeout), the send proceeds, and
 * dies as ERROR_UNKNOWN 1.7-50s later. Over 72h, chats failing with either
 * `participants=0` or `chat.isReadOnly` produced 2,955 failures and ZERO
 * deliveries across 90 chats; not one delivery was recorded anywhere with
 * participants=0. So on a failed/timed-out query we decline in that state
 * rather than spend the send budget reaching a foregone conclusion — and get
 * retried. Any other state still falls through, unchanged.
 */

const GROUP_METADATA_QUERY_TIMEOUT_MS = 8000;

/** #534 — matches the guard refusals below (not writable, unavailable, membership unverified); the send thunk uses it to attach the send trace. */
export const GROUP_GUARD_DECLINE_RE = /Cannot send to this group \(|Could not confirm this WhatsApp account can post in group \(/;

/** Thrown message renders VERBATIM to the customer via sendFailureError —
 *  keep it a plain statement of the real problem, no internals. */
export class GroupUnavailableError extends Error {
    constructor(chatId: string) {
        super(
            `Cannot send to this group (${chatId}) — this WhatsApp account is not a participant, or the group no longer exists`,
        );
        this.name = 'GroupUnavailableError';
    }
}

/** The hydration query never answered AND the chat is not writable by this account.
 *  Distinct from GroupUnavailableError: there the server answered and revealed no
 *  membership; here it never answered, and we are declining on the chat's own state. */
export class GroupNotWritableError extends Error {
    constructor(chatId: string) {
        super(
            `Cannot send to this group (${chatId}) — this WhatsApp account cannot post in it ` +
            `(the group is read-only for this account, or it is no longer a participant)`,
        );
        this.name = 'GroupNotWritableError';
    }
}

/** #657 — the metadata query never answered and the chat shows empty metadata but is NOT read-only. That is missing
 *  evidence, not proof the account cannot post, so the text avoids every phrase the backend reads as the customer's
 *  (`Cannot send to this group`, `cannot post in`, `not a participant`): it classifies `system`, with this cause. */
export class GroupMembershipUnverifiedError extends Error {
    constructor(chatId: string) {
        super(`Could not confirm this WhatsApp account can post in group (${chatId}) — WhatsApp did not answer the group membership check`);
        this.name = 'GroupMembershipUnverifiedError';
    }
}

interface GuardStore {
    GroupQueryAndUpdate?: (arg: { id: string }) => Promise<unknown>;
    GroupMetadata?: { get?: (id: string) => any };
}

/** Membership check: the chat reference may be stale after the query, so
 *  also consult the GroupMetadata collection (where the query writes). */
function hasGroupParticipants(chat: any, store: GuardStore, chatId: string): boolean {
    const direct = chat?.groupMetadata?.participants;
    if (typeof direct?.length === 'number' && direct.length > 0) return true;
    const fromCollection = store?.GroupMetadata?.get?.(chatId)?.participants;
    return typeof fromCollection?.length === 'number' && fromCollection.length > 0;
}

/** `chat.isReadOnly` is WhatsApp's own answer to "can the CURRENT account post here?" —
 *  it is NOT the group-level `announce` setting (an owner of an announce-only group can
 *  still post, and their chat is not read-only). Strict `=== true` so an absent or
 *  unreadable flag is never treated as a refusal. */
function isChatReadOnly(chat: any): boolean {
    return chat?.isReadOnly === true;
}

/**
 * Metadata is PRESENT and states zero participants. On its own that is NOT proof of dead membership when the
 * refresh query did not answer (#657: 7 of 26 such declined chats delivered afterwards), so the no-answer decline
 * on this signal alone no longer declines (#746: it proceeds; GroupMembershipUnverifiedError is kept only so the decline matcher and backend classifier still recognise the text); only readOnly (WhatsApp's own answer) or a refreshed
 * empty list is conclusive.
 *
 * Deliberately NOT the same as `!hasGroupParticipants()`, which is also true when metadata
 * is simply ABSENT. That distinction is the whole safety margin of this guard, and the
 * numbers are not close (72h of group media):
 *
 *   metadata present, 0 participants  ->  1,507 failures,      0 deliveries   (dead)
 *   metadata absent entirely          ->  4,597 failures, 12,146 deliveries   (73% SUCCEED)
 *
 * A hollow chat is the ordinary state of a dormant group and is exactly what #118's
 * hydration exists to repair; declining on it would fail a population that mostly works.
 * Worse, hydration timeouts are correlated — one WhatsApp-side slowdown times out many
 * queries at once — so treating "no answer" as "dead" could fail a large batch of healthy
 * sends simultaneously. Only an explicit empty participant list counts here.
 */
function hasEmptyGroupMetadata(chat: any, store: GuardStore, chatId: string): boolean {
    const gm = chat?.groupMetadata ?? store?.GroupMetadata?.get?.(chatId);
    if (!gm) return false; // absent ≠ empty: no evidence, so no refusal
    const len =
        gm?.participants?.length ??
        gm?.participants?._models?.length ??
        gm?.participants?.getModelsArray?.().length;
    return len === 0;
}

/** #534 — which check refused the send. One token per decline path below. */
export type GroupGuardDeclineBranch =
    | 'readOnly' | 'emptyMeta' | 'readOnly+emptyMeta'   // query did not answer
    | 'noParticipants' | 'readOnlyAfterRefresh'        // query answered
    | 'notMember' | 'notMemberUnverified';              // WhatsApp's own iAmMember() answered false (after / without a refresh)

/** #661 — WhatsApp's own answer to "is the CURRENT account in this group?". Strict `=== false`: a missing method,
 *  a throw or any non-boolean is unreadable, never a refusal (same rule as `isChatReadOnly`). Reads the collection
 *  first (where a refresh writes), then the chat's own reference. Measured over the `ERROR_UNKNOWN` group-media
 *  failures since 10-23: 214 of 235 carried member=false with readOnly=false and 1-2 cached participants. */
function isKnownNonMember(chat: any, store: GuardStore, chatId: string): boolean {
    try {
        const gm = store?.GroupMetadata?.get?.(chatId) ?? chat?.groupMetadata;
        return gm?.participants?.iAmMember?.() === false;
    } catch {
        return false;
    }
}

/** Participant count the guard can see (chat ref, else the collection), or `?` when unreadable. */
function visibleParticipantCount(chat: any, store: GuardStore, chatId: string): string {
    const gm = chat?.groupMetadata ?? store?.GroupMetadata?.get?.(chatId);
    const len = gm?.participants?.length ?? gm?.participants?._models?.length;
    return typeof len === 'number' ? String(len) : '?';
}

/**
 * #534 — the decline as one trace token: the branch that fired, how the query ended, and the chat
 * state the decision read. A declined send used to carry no trace at all, so a false positive (the
 * same chat writable minutes later) could not be told apart by branch. Ids and text never enter it.
 */
export function describeGroupGuardDecline(
    branch: GroupGuardDeclineBranch,
    query: 'ok' | 'timeout' | 'error',
    chat: any,
    store: GuardStore,
    chatId: string,
): string {
    const flag = (v: unknown): string => (typeof v === 'boolean' ? String(v) : '?');
    return `groupGuard:decline[branch=${branch},query=${query},` +
        `hasMeta=${!!(chat?.groupMetadata ?? store?.GroupMetadata?.get?.(chatId))},` +
        `participants=${visibleParticipantCount(chat, store, chatId)},` +
        `readOnly=${flag(chat?.isReadOnly)},pendingLoad=${flag(chat?.pendingInitialLoading)}]`;
}

export async function ensureGroupHydratedForMediaSend(
    chat: any,
    chatId: string,
    store: GuardStore = requireStore() as any,
    /** #534 — receives the decline token (the caller stamps it on the send trace). Never throws into the guard. */
    onDecline?: (detail: string) => void,
): Promise<void> {
    const decline = (branch: GroupGuardDeclineBranch, query: 'ok' | 'timeout' | 'error'): void => {
        try { onDecline?.(describeGroupGuardDecline(branch, query, chat, store, chatId)); } catch { /* diagnostics only */ }
    };
    /** A state that used to decline but is now attempted: same token, `proceed` instead of `decline`. */
    const proceed = (branch: GroupGuardDeclineBranch, query: 'ok' | 'timeout' | 'error'): void => {
        try {
            onDecline?.(
                describeGroupGuardDecline(branch, query, chat, store, chatId).replace('groupGuard:decline[', 'groupGuard:proceed['),
            );
        } catch { /* diagnostics only */ }
    };
    if (chatKindOf(chatId) !== 'group') return;
    // A hydrated chat used to return here unconditionally, so a read-only chat with
    // participants never reached the readOnly check below and died as ERROR_UNKNOWN on
    // every retry (5.9.40, 10-08→10-10: 34 chats / 111 messages, zero deliveries). A
    // read-only chat now goes through the refresh query too, and is declined if WhatsApp
    // still says this account cannot post once metadata is fresh.
    // #661 — cached participants and readOnly=false do not mean the account is still in the group: iAmMember() can
    // say false on a stale chat. A cached false is NOT conclusive (it may be a partial or stale list), so it goes
    // through the same refresh as a read-only chat and is declined only if WhatsApp still says false afterwards.
    const cachedNonMember = isKnownNonMember(chat, store, chatId);
    if (hasGroupParticipants(chat, store, chatId) && !isChatReadOnly(chat) && !cachedNonMember) return;

    const query = store?.GroupQueryAndUpdate;
    if (typeof query !== 'function') return;

    let timeoutTimer: ReturnType<typeof setTimeout> | undefined;
    try {
        await Promise.race([
            query({ id: chatId }),
            new Promise((_, reject) => {
                timeoutTimer = setTimeout(
                    () => reject(new Error('group metadata query timed out')),
                    GROUP_METADATA_QUERY_TIMEOUT_MS,
                );
            }),
        ]);
    } catch (err) {
        // The hydration query failed or (far more often) timed out.
        //
        // This used to fall through unconditionally, on the principle that the guard must
        // never break a send that would have worked. Production says that principle does not
        // hold in one specific state. Measured 2026-08-04 over 72h of group media sends:
        // chats that are read-only with no participants produced 2,955 failures and ZERO
        // deliveries across 90 chats. The traces show the shape exactly — `media:groupGuard`
        // sits at ~8004ms (this timeout), the send then proceeds anyway, and dies as
        // `ERROR_UNKNOWN` after a further 1.7-50s. Falling through is not protective there;
        // it just spends the send budget to reach a foregone conclusion, then gets retried.
        //
        // Decline only on evidence that the chat cannot take the message. Read-only is WhatsApp's own
        // answer (GroupNotWritableError); empty metadata with no answer is only suggestive (#657), so it
        // does NOT decline at all since #746 (only the read-only reading below does). The participants=0 figures that follow are the 08-04 cohort; the 10-28 cohort (30 groups, ~13.9k prior deliveries) contradicts treating them as proof. Either
        // signal alone is conclusive — requiring both would have caught 825 of 2,960 failures
        // (28%) — but each must be an affirmative reading, never an absence of one. Measured
        // per chat over 72h, counting only chats that ever delivered anything:
        //   participants=0 only   — 49 chats, 1,507 failures, 0 deliveries
        //   read-only only        — 25 chats,   623 failures, 0 deliveries  (+1 chat that DID deliver)
        //   both                  — 16 chats,   825 failures, 0 deliveries
        // Coverage is what licenses each: chatReadOnly is recorded on 96% of deliveries
        // (readOnly=true fails 228/234), and participants=0 means metadata was READ and was
        // empty. The "metadata absent" case is explicitly excluded — see hasEmptyGroupMetadata.
        //
        // Everything else still falls through: the containment principle is right wherever
        // we lack evidence to the contrary. False-positive cost at this scope is 1 chat in 91.
        // #880 (2026-10-30) narrows this further: readOnly next to an EMPTY participant list (the "both" cohort
        // above) no longer declines, because both readings come off the same empty list. It proceeds and is traced.
        const readOnly = isChatReadOnly(chat);
        const emptyMeta = hasEmptyGroupMetadata(chat, store, chatId);
        const queryOutcome = /timed out/.test(String((err as Error)?.message ?? '')) ? 'timeout' : 'error';
        // #880 — readOnly next to an EMPTY participant list is read off that same empty list, so it says nothing
        // about this account (like iAmMember(), #746). Only readOnly on a chat whose list is not empty declines.
        if (readOnly && !emptyMeta) {
            // WhatsApp's own answer that this account cannot post here: the only affirmative reading left on the
            // no-answer path (#746). Everything below is an absence of evidence.
            decline('readOnly', queryOutcome);
            console.warn(
                `[group-media-guard] hydration did not answer for ${chatId} (readOnly=true) ` +
                `— declining instead of attempting a send that cannot land`,
            );
            throw new GroupNotWritableError(chatId);
        }
        // #746 — NOT a decline. Empty metadata (a chat still loading, participants=0, pendingLoad=true) or a cached
        // iAmMember()===false on a stale list, with a refresh that never answered, is missing evidence, not proof of
        // non-membership: iAmMember() is trivially false on an empty participant list. 10-28, ext 5.9.64: 151 of
        // these declines hit 30 groups that had delivered ~13.9k times before (readOnly=false, announce=false on
        // every trace), because a correlated GroupQueryAndUpdate stall declined every dormant group in a campaign
        // and each retry hit the same stall. Attempt the send instead (the pre-#94 behaviour for this state); a
        // chat that truly cannot take it still fails there, with the real send error rather than this guess.
        // Metadata ABSENT with no cached false is the ordinary dormant-group state and carries no token (as before).
        if (readOnly || cachedNonMember || emptyMeta) {
            proceed(readOnly ? 'readOnly+emptyMeta' : cachedNonMember ? 'notMemberUnverified' : 'emptyMeta', queryOutcome);
        }
        console.warn(
            `[group-media-guard] GroupQueryAndUpdate failed for ${chatId} — proceeding with the send as-is:`,
            err,
        );
        return;
    } finally {
        clearTimeout(timeoutTimer);
    }

    if (!hasGroupParticipants(chat, store, chatId)) {
        // #880 — the refresh answered but the list is still EMPTY. That is not proof of non-membership: one owner's
        // 19 groups, each with 12-193 confirmed deliveries, came back participants=0 / readOnly=true / member=false
        // (all three read off the same empty list). Attempt the send; a chat that truly cannot take it fails there
        // with the real error. Only metadata ABSENT after an answered refresh (#118: WhatsApp reveals group metadata
        // only to members) still declines.
        if (hasEmptyGroupMetadata(chat, store, chatId)) {
            proceed('noParticipants', 'ok');
            console.warn(`[group-media-guard] ${chatId}: refresh answered with an empty participant list — proceeding with the send`);
            return;
        }
        decline('noParticipants', 'ok');
        throw new GroupUnavailableError(chatId);
    }
    if (isKnownNonMember(chat, store, chatId)) {
        decline('notMember', 'ok');
        console.warn(`[group-media-guard] ${chatId}: WhatsApp still reports this account is not a member after a metadata refresh — declining instead of attempting a send that cannot land`);
        throw new GroupUnavailableError(chatId);
    }
    if (isChatReadOnly(chat)) {
        decline('readOnlyAfterRefresh', 'ok');
        console.warn(
            `[group-media-guard] ${chatId} is still read-only after a metadata refresh ` +
            `— declining instead of attempting a send that cannot land`,
        );
        throw new GroupNotWritableError(chatId);
    }
}
